import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SeekerProfileService, SeekerProfile } from '../../../services/seeker-profile.service';
import { TranslatePipe } from '../../../../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-seeker-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './profile.html',
  styleUrl: './profile.scss',
})
export class SeekerProfileComponent implements OnInit {
  private service = inject(SeekerProfileService);

  profile: SeekerProfile | null = null;
  loading = true;
  saving = false;
  error: string | null = null;
  successMsg: string | null = null;

  // Form fields
  firstName = '';
  lastName = '';
  phoneNumber = '';
  lang = 'en';
  aboutMe = '';
  showProfile = 'public';
  notifEmail = false;
  notifApp = false;
  expectedBudget: number | null = null;
  preferredArea = '';
  propertyType = '';
  purposeOfSearch = '';

  imageFile: File | null = null;
  imagePreview: string | null = null;

  readonly showProfileOptions = ['public', 'registered', 'private'];
  readonly propertyTypes = ['apartment', 'villa', 'studio', 'office', 'land', 'other'];
  readonly purposes = ['rent', 'sale'];
  readonly langs = ['en', 'ar'];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.service.getProfile().subscribe({
      next: (res) => {
        this.profile = res.resProfile;
        this.patchForm(res.resProfile);
        this.loading = false;
      },
      error: (err) => {
        this.error = err.message || 'Failed to load profile';
        this.loading = false;
      },
    });
  }

  private patchForm(p: SeekerProfile): void {
    this.firstName = p.firstName ?? '';
    this.lastName = p.lastName ?? '';
    this.phoneNumber = p.phoneNumber ?? '';
    this.lang = p.lang ?? 'en';
    this.aboutMe = p.aboutMe ?? '';
    this.showProfile = p.ShowProfile ?? 'public';
    this.notifEmail = p.notificationSettings?.email ?? false;
    this.notifApp = p.notificationSettings?.app ?? false;
    this.expectedBudget = p.expectedBudget ?? null;
    this.preferredArea = p.preferredArea ?? '';
    this.propertyType = p.propertyType ?? '';
    this.purposeOfSearch = p.purposeOfSearch ?? '';
  }

  onImageChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.imageFile = file;
    const reader = new FileReader();
    reader.onload = (e) => (this.imagePreview = e.target?.result as string);
    reader.readAsDataURL(file);
  }

  save(): void {
    this.saving = true;
    this.error = null;
    this.successMsg = null;

    const fd = new FormData();
    fd.append('firstName', this.firstName);
    fd.append('lastName', this.lastName);
    fd.append('phoneNumber', this.phoneNumber);
    fd.append('lang', this.lang);
    fd.append('aboutMe', this.aboutMe);
    fd.append('showProfile', this.showProfile);
    fd.append('notificationSettings[email]', String(this.notifEmail));
    fd.append('notificationSettings[app]', String(this.notifApp));
    if (this.expectedBudget !== null) fd.append('expectedBudget', String(this.expectedBudget));
    fd.append('preferredArea', this.preferredArea);
    fd.append('propertyType', this.propertyType);
    fd.append('purposeOfSearch', this.purposeOfSearch);
    if (this.imageFile) fd.append('profileImage', this.imageFile);

    this.service.updateProfile(fd).subscribe({
      next: (res) => {
        this.profile = res.resProfile;
        this.patchForm(res.resProfile);
        this.imageFile = null;
        this.imagePreview = null;
        this.saving = false;
        this.successMsg = res.message || 'Profile updated successfully';
        setTimeout(() => (this.successMsg = null), 3000);
      },
      error: (err) => {
        this.error = err.message || 'Failed to update profile';
        this.saving = false;
      },
    });
  }

  get avatarUrl(): string {
    return this.imagePreview || this.profile?.profileImage || '';
  }

  get fullName(): string {
    return `${this.profile?.firstName ?? ''} ${this.profile?.lastName ?? ''}`.trim();
  }
}
